import React, { FC, useState, useEffect, useMemo, useRef } from 'react';
import ValuationsTable from './ValuationsTable';
import ValuationsUserInput from './ValuationsUserInput';
import { LoadingSpinner } from '../../icons/OurIcons';
import { ValuationsType, UserValuationType } from '../../../utility/types';
import { formatFloat } from '../../../utility/utility_functions';
import { formatMarketValueRate } from '../../../utility/valuationSettings';
import { useDecisionFormContext } from '../../../contexts/DecisionMaking/DecisionFormContext';
import { usePropertyContext } from '../../../contexts/DecisionMaking/PropertyContext';
import { useAvmContext } from '../../../contexts/DecisionMaking/AvmContext';

const renderGridItem = (title: string, value: string, id: string) => {
  return (
    <div className='flex justify-center items-center border-2 rounded-xl p-3 mb-5'>
      <div>
        <p className='text-xs'>{title}</p>
        <p id={id} className='pt-1 text-base font-bold'>{value ?? '-'}</p>
      </div>
    </div>
  );
};

const ValuationsContainer: FC = () => {
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    state: {
      estimatedValue,
      estimatedValuePerSqft,
      valuationType,
    },
    dispatch,
  } = useDecisionFormContext();

  const {
    propertyDetails,
    sqft,
    isLoadingPropertyDetails,
  } = usePropertyContext();

  const {
    soldAvmResult,
    activeAvmResult,
    soldCompsSix,
    soldAvmEstimatedValue,
    soldAvmEstimatedValuePerSqft,
    marketValueRate,
  } = useAvmContext();

  const [ userEstimatedValue, setUserEstimatedValue ] = useState(estimatedValue ?? null);
  const [ userEstimatedValuePerSqft, setUserEstimatedValuePerSqft ] = useState(estimatedValuePerSqft ?? null);
  const [ userSelectedValuationType, setUserSelectedValuationType ] = useState<UserValuationType>(UserValuationType.ValuationAmount);
  const [ inputValue, setInputValue ] = useState(null);

  const isLoading = useMemo(
    () => isLoadingPropertyDetails,
    [ isLoadingPropertyDetails ]
  );

  useEffect(() => {
    if (propertyDetails && !isLoading) {
      if (valuationType === ValuationsType.UserInput && estimatedValue !== null && estimatedValuePerSqft !== null) {
        setInputValue(estimatedValue);
        setUserSelectedValuationType(UserValuationType.ValuationAmount);
        setUserEstimatedValue(estimatedValue);
        setUserEstimatedValuePerSqft(estimatedValuePerSqft);
      }
      else {
        setInputValue(null);
        setUserEstimatedValue(null);
        setUserEstimatedValuePerSqft(null);
      }
      // not sure if these conditions are correct
    }
  }, [ propertyDetails, isLoading ]);

  useEffect(() => {
    if (inputValue === null || inputValue === estimatedValue) {
      return;
    }
    if (userSelectedValuationType === UserValuationType.ValuationAmount) {
      let dollPerSqFt = (inputValue / sqft);
      setUserEstimatedValue(inputValue);
      setUserEstimatedValuePerSqft(dollPerSqFt);

      console.log('ValuationsContainer SET_FORM_FIELD useEffect - inputValue', inputValue);

      dispatch({ type: 'SET_FORM_FIELD', payload: [
        { field: 'valuationType', value: ValuationsType.UserInput },
        { field: 'estimatedValue', value: inputValue },
        { field: 'estimatedValuePerSqft', value: dollPerSqFt } ] });
    }
    // Calculate val amount when user provides $/sq ft
    else if (userSelectedValuationType === UserValuationType.DollarPerSqFt) {
      if (sqft === 0) {
        console.error('Sqft for the property is 0, cannot calculate valuation amount');
        return;
      }
      let valAmount = (inputValue * sqft);
      setUserEstimatedValuePerSqft(inputValue);
      setUserEstimatedValue(valAmount);
      console.log('ValuationsContainer SET_FORM_FIELD useEffect - inputValue', inputValue);

      dispatch({ type: 'SET_FORM_FIELD', payload: [
        { field: 'estimatedValue', value: valAmount },
        { field: 'estimatedValuePerSqft', value: inputValue },
        { field: 'valuationType', value: ValuationsType.UserInput },
      ] });
    }
    // Ensure input stays focused after value update
    if (inputRef.current) {
      // Defer focus reset to after render cycle
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [ inputValue ]);

  const handleResetUserInput = () => {
    // Clear user inputs and reset highlighted values to REIX estimates
    setInputValue(null);
    setUserEstimatedValue(null);
    setUserEstimatedValuePerSqft(null);
    setUserSelectedValuationType(UserValuationType.ValuationAmount);

    dispatch({ type: 'SET_FORM_FIELD', payload: [ { field: 'valuationType', value: ValuationsType.InactiveAvm },
      { field: 'estimatedValue', value: soldAvmEstimatedValue },
      { field: 'estimatedValuePerSqft', value: soldAvmEstimatedValuePerSqft } ] });
  };

  const handleRowClick = (arvEst: number, arvSf: number, rowName: ValuationsType) => {
    dispatch({ type: 'SET_FORM_FIELD', payload: [
      { field: 'valuationType', value: rowName },
      { field: 'estimatedValue', value: arvEst },
      { field: 'estimatedValuePerSqft', value: arvSf } ] });
  };

  const ValuationsContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-96">
          <LoadingSpinner />
        </div>
      );
    }

    if (!soldAvmResult) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">No valuation data available</div>
        </div>
      );
    }

    return (
      <div>
        <div className="rounded-xl mx-auto mt-3">
          <ValuationsTable
            propertyDetails={propertyDetails}
            sqft={sqft}
            soldAvmResult={soldAvmResult}
            activeAvmResult={activeAvmResult}
            soldCompsSix={soldCompsSix}
            userSelectedValuationType={userSelectedValuationType}
            userEstimatedValue={userEstimatedValue}
            userEstimatedValuePerSqft={userEstimatedValuePerSqft}
            activeValuationType={valuationType}
            selectedValuation={estimatedValue}
            handleRowClick={(arvEst, arvSf, rowName) => handleRowClick(arvEst, arvSf, rowName)}
          />
        </div>

        {!!marketValueRate && (
          <p id='valuations-market-value-rate-note' className='mx-2 mt-2 text-xs text-gray-500'>
            Active and Sold ARV include your Market Value Rate of {formatMarketValueRate(marketValueRate)}.
          </p>
        )}

        {/* User Input */}

        <ValuationsUserInput
          userSelectedValuationType={userSelectedValuationType}
          setUserSelectedValuationType={setUserSelectedValuationType}
          setInputValue={setInputValue}
          inputValue={inputValue}
          handleResetUserInput={handleResetUserInput}
          inputRef={inputRef}
        />

        <div id='valuations-input-container-valuations' className='grid grid-cols-2 m-5 gap-x-5 text-center'>
          {estimatedValue ? renderGridItem('ARV', `\$${formatFloat(estimatedValue, 0)}`, 'valuations-input-container-valuations-arv-value') : renderGridItem('ARV', '-', 'valuations-input-container-valuations-arv-value')}
          {estimatedValuePerSqft ? renderGridItem('ARV $/Sqft', `\$${formatFloat(estimatedValuePerSqft, 2)}`, 'valuations-input-container-valuations-arv-sqft-value') : renderGridItem('ARV $/Sqft', '-', 'valuations-input-container-valuations-arv-sqft-value')}
        </div>
      </div>
    );
  };

  // ------------------------------
  // MAIN RETURN
  // ------------------------------
  return (
    <div id='valuations-container' className="w-full content-center justify-content-center mb-3">
      <div className="flex justify-between m-3 items-center">
        <h3 className="whitespace-nowrap text-extrabold text-lg text-indigo-500">
          Valuations
        </h3>
      </div>
      <hr className="mt-3 mb-4 mx-3" />
      <ValuationsContent />
    </div>
  );
};

export default ValuationsContainer;
